/* ──────────────────────────────────────────────────────────────
   ThursdayFridayCalendar.jsx  ·  Calendar component for Thursday/Friday availability
   ──────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./ThursdayCalendar.css";
import intakeIcon from "../../../images/TPC Icon intake block.png";

// Function to generate upcoming Thursday dates for a specific month
const generateUpcomingIntakeDates = (selectedYear, selectedMonth) => {
  const dates = [];
  const today = new Date();

  // Get the first day of the selected month
  const monthStart = new Date(selectedYear, selectedMonth, 1);
  const monthEnd = new Date(selectedYear, selectedMonth + 1, 0);

  // Start from the first day of the month or tomorrow, whichever is later
  const startDate = new Date(
    Math.max(monthStart.getTime(), today.getTime() + 86400000)
  ); // +1 day

  // Loop through the selected month
  const currentDate = new Date(startDate);
  while (currentDate <= monthEnd) {
    const dayOfWeek = currentDate.getDay();

    // Check if it's Thursday (4) - 2-day intake starts on Thursday
    if (dayOfWeek === 4) {
      const thursdayDate = new Date(currentDate);
      const fridayDate = new Date(currentDate);
      fridayDate.setDate(fridayDate.getDate() + 1);

      const fullDate = thursdayDate.toISOString().split("T")[0];
      const formattedThursday = thursdayDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const formattedFriday = fridayDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });

      dates.push({
        fullDate,
        formattedDate: formattedThursday,
        formattedFriday: formattedFriday,
        isThursday: true,
      });
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

// Function to generate 12 months starting from current month
const generateMonthOptions = () => {
  const months = [];
  const today = new Date();

  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push({
      value: `${date.getFullYear()}-${date.getMonth()}`,
      label: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      }),
      year: date.getFullYear(),
      month: date.getMonth(),
    });
  }

  return months;
};

export default function ThursdayCalendar({
  selectedThursdays = [],
  onSelectionChange,
}) {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${today.getFullYear()}-${today.getMonth()}`
  );
  const monthOptions = generateMonthOptions();

  // Parse selected month
  const [selectedYear, selectedMonthIndex] = selectedMonth
    .split("-")
    .map(Number);
  const upcomingDates = generateUpcomingIntakeDates(
    selectedYear,
    selectedMonthIndex
  );

  const selectedDate =
    selectedThursdays.length > 0 ? selectedThursdays[0] : null;
  const [bookingData, setBookingData] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch booking data for all upcoming dates
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const functions = getFunctions();
        const readIntakes = httpsCallable(functions, "firestoreOperations");

        // Fetch all ai-intakes documents
        const result = await readIntakes({
          operation: "readCollection",
          company: "TPC_Group",
          collectionBrand: "TechPerformanceCentre",
          systemName: "ai-intakes",
        });

        if (result.data && result.data.documents) {
          // Create a map of date -> booking info
          const bookings = {};
          result.data.documents.forEach((doc) => {
            if (doc.intakeDate) {
              if (!bookings[doc.intakeDate]) {
                bookings[doc.intakeDate] = {
                  count: 0,
                  participants: [],
                };
              }
              bookings[doc.intakeDate].count++;
              bookings[doc.intakeDate].participants.push({
                email: doc.email,
                name: doc.name,
                applicationType: doc.applicationType,
              });
            }
          });
          setBookingData(bookings);
        }
      } catch (error) {
        console.error("Error fetching intake bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleDateClick = (fullDate, isBooked) => {
    // Don't allow selection if date is fully booked (e.g., max 20 bookings)
    if (isBooked && bookingData[fullDate]?.count >= 20) {
      return;
    }

    if (onSelectionChange) {
      // Single selection - replace the entire array with just this date
      if (selectedDate === fullDate) {
        // Deselect if clicking the same date
        onSelectionChange([]);
      } else {
        // Select this date (replacing any previous selection)
        onSelectionChange([fullDate]);
      }
    }
  };

  return (
    <div className="thursday-calendar">
      <div className="thursday-calendar-header">
        <h4>Enrollment Dates</h4>
        <p>Select ONE enrollment date (2-day program: Thursday & Friday)</p>

        {/* Month Filter */}
        <div className="month-filter">
          <label htmlFor="month-select">Filter by Month:</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="month-select"
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="intake-dates-list">
        {loading ? (
          <p className="loading-message">Loading availability...</p>
        ) : upcomingDates.length > 0 ? (
          upcomingDates.map((dateInfo) => {
            const bookingInfo = bookingData[dateInfo.fullDate];
            const bookingCount = bookingInfo?.count || 0;
            const isFullyBooked = bookingCount >= 20;
            const availableSpots = 20 - bookingCount;

            return (
              <div
                key={dateInfo.fullDate}
                className={`intake-date-option ${
                  selectedDate === dateInfo.fullDate ? "selected" : ""
                } ${isFullyBooked ? "fully-booked" : ""} thursday`}
                onClick={() =>
                  handleDateClick(dateInfo.fullDate, bookingCount > 0)
                }
                style={{ cursor: isFullyBooked ? "not-allowed" : "pointer" }}
              >
                <img src={intakeIcon} alt="Intake" className="intake-icon" />
                <div className="intake-date-content">
                  <div className="intake-date-day">Duration: 2 days</div>
                  <div className="intake-date-full">
                    {dateInfo.formattedDate}
                  </div>
                  <div className="intake-date-friday">
                    & {dateInfo.formattedFriday}
                  </div>
                  {bookingCount > 0 && (
                    <div className="intake-booking-status">
                      {isFullyBooked ? (
                        <span className="fully-booked-badge">Fully Booked</span>
                      ) : (
                        <span className="spots-available">
                          {availableSpots}{" "}
                          {availableSpots === 1 ? "spot" : "spots"} available
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="no-dates-message">
            No enrollment dates available for{" "}
            {monthOptions.find((m) => m.value === selectedMonth)?.label ||
              "this month"}
            .
          </p>
        )}
      </div>
    </div>
  );
}
